import { dndHandleColumnId } from "@org-avp/avp-avengers-ui-shared-utils";
import { createSlice, Dispatch, PayloadAction } from "@reduxjs/toolkit";
import { GetStateFn } from "../../utils/dispatchUtils";

export const tmmViewOptionsSliceName = "tmm";

export enum TmmViewTabKey {
  SERIAL = "serial",
  INDIVIDUAL = "individual",
}

export enum TmmViewSerialColumn {
  ADI = "adi",
  INDEX = "index",
  LABEL = "label",
  MATERIAL_CODE = "materialCode",
  MATERIAL_TYPE = "materialType",
  TARGET = "target",
  TRIGGER = "trigger",
  VALIDITY = "validity",
}

export enum TmmViewIndividualColumn {
  ID_NAME = "idName",
  ID_COLOR_NAME = "idColorName",
  INDEX = "index",
  LABEL = "label",
  MATERIAL_CODE = "materialCode",
  MATERIAL_TYPE = "materialType",
  TRIGGER = "trigger",
  VALIDITY = "validity",
}

export const tmmViewSerialColumnIds = [
  dndHandleColumnId,
  TmmViewSerialColumn.TARGET,
  TmmViewSerialColumn.MATERIAL_CODE,
  TmmViewSerialColumn.ADI,
  TmmViewSerialColumn.VALIDITY,
  TmmViewSerialColumn.TRIGGER,
  TmmViewSerialColumn.LABEL,
  TmmViewSerialColumn.MATERIAL_TYPE,
  TmmViewSerialColumn.INDEX,
];

export const tmmViewIndividualColumnIds = [
  TmmViewIndividualColumn.ID_NAME,
  TmmViewIndividualColumn.ID_COLOR_NAME,
  TmmViewIndividualColumn.MATERIAL_CODE,
  TmmViewIndividualColumn.VALIDITY,
  TmmViewIndividualColumn.TRIGGER,
  TmmViewIndividualColumn.LABEL,
  TmmViewIndividualColumn.MATERIAL_TYPE,
  TmmViewIndividualColumn.INDEX,
];

export type TmmViewOptionsState = {
  activeTabKey: TmmViewTabKey;
  serialColumnIds: string[];
  individualColumnIds: string[];
};

const persistedTmmViewOptions = localStorage.getItem(tmmViewOptionsSliceName);
const persistedState = persistedTmmViewOptions ? JSON.parse(persistedTmmViewOptions) as TmmViewOptionsState : null;
const fallback: TmmViewOptionsState = {
  activeTabKey: TmmViewTabKey.SERIAL,
  serialColumnIds: tmmViewSerialColumnIds,
  individualColumnIds: tmmViewIndividualColumnIds,
};
const initialState: TmmViewOptionsState = persistedState ?? fallback;
// if columns does not exist, set default fallback value
if (initialState.serialColumnIds === undefined || initialState.serialColumnIds.length === 0) {
  initialState.serialColumnIds = fallback.serialColumnIds;
}
if (initialState.individualColumnIds === undefined || initialState.individualColumnIds.length === 0) {
  initialState.individualColumnIds = fallback.individualColumnIds;
}
if (initialState.activeTabKey === undefined) {
  initialState.activeTabKey = fallback.activeTabKey;
}

const tmmViewOptionsSlice = createSlice({
  name: tmmViewOptionsSliceName,
  initialState,
  reducers: {
    setTmmViewActiveTab(state: TmmViewOptionsState, action: PayloadAction<TmmViewTabKey>) {
      state.activeTabKey = action.payload;
    },
    setTmmViewSerialColumnIds(state: TmmViewOptionsState, action: PayloadAction<string[]>) {
      state.serialColumnIds = action.payload;
    },
    setTmmViewIndividualColumnIds(state: TmmViewOptionsState, action: PayloadAction<string[]>) {
      state.individualColumnIds = action.payload;
    },
  },
});

export type TmmViewOptionsRootState = { [tmmViewOptionsSliceName]: TmmViewOptionsState };
export const selectTmmViewOptions = (state: TmmViewOptionsRootState) => state[tmmViewOptionsSliceName];

export const setTmmViewActiveTabKey = (tabKey: TmmViewTabKey) => (dispatch: Dispatch, getState: GetStateFn) => {
  dispatch(tmmViewOptionsSlice.actions.setTmmViewActiveTab(tabKey));
  persistState(getState);
};
export const setTmmViewSerialColumnIds = (columns: string[]) => (dispatch: Dispatch, getState: GetStateFn) => {
  dispatch(tmmViewOptionsSlice.actions.setTmmViewSerialColumnIds(columns));
  persistState(getState);
};
export const setTmmViewIndividualColumnIds = (columns: string[]) => (dispatch: Dispatch, getState: GetStateFn) => {
  dispatch(tmmViewOptionsSlice.actions.setTmmViewIndividualColumnIds(columns));
  persistState(getState);
};

function persistState(getState: GetStateFn) {
  localStorage.setItem(tmmViewOptionsSliceName, JSON.stringify(
    selectTmmViewOptions(getState()),
  ));
}

export const tmmViewOptionsReducer = tmmViewOptionsSlice.reducer;
